/**
 * The tool calls of an agent turn, shaped for a transcript row.
 *
 * Shared by the two transcripts that render them: the ledger (Workshop) and the
 * M7 rail's conversation. Both need the same answer to "which tool events get a
 * generic row, which render their own card, and which repeat calls collapse".
 */
import type { ChatToolEvent, StreamingToolEvent } from "@/types/chat.type";
import { ASK_USER_QUESTION_TOOL } from "./askUserQuestion";
import { VERIFY_WITH_USER_TOOL } from "./verifyWithUser";
import { PLAN_MONTH_TOOL } from "./planMonth";
import { ATTACH_VIDEO_TOOL } from "./attachedVideos";
import { GENERATE_IMAGE_TOOL } from "./generatedMediaCard";
import { CREATE_ARTIFACT_TOOL } from "./createdArtifactCard";
import { getToolDisplayLabel, getToolInstrumentLabel } from "./toolCallDisplay";

/** Tool events the rail renders as their own UI instead of a generic act line. */
export function isCustomRenderedTool(toolName: string): boolean {
  return toolName === ASK_USER_QUESTION_TOOL || toolName === VERIFY_WITH_USER_TOOL || toolName === PLAN_MONTH_TOOL;
}

/** Long-running waits have no % completion — keep the live tick, skip the fill bar. */
const TOOLS_WITHOUT_PROGRESS_BAR = new Set(["WaitForAgentRuns", "WaitForAgents"]);

export function toolShowsProgressBar(toolName: string): boolean {
  return !TOOLS_WITHOUT_PROGRESS_BAR.has(toolName);
}

export type RailToolItem = {
  key: string;
  tool: string;
  label: string;
  status: "run" | "ok" | "err";
  showProgress: boolean;
  event: ChatToolEvent | StreamingToolEvent;
};

export type GroupedRailTool = RailToolItem & { count: number };

/** Tools that render a unique preview under the row — never collapsed into ×N. */
const PREVIEWABLE_TOOLS = new Set([GENERATE_IMAGE_TOOL, CREATE_ARTIFACT_TOOL, ATTACH_VIDEO_TOOL]);

/** Collapse consecutive tool rows that share the same instrument + label. */
export function groupConsecutiveTools(tools: RailToolItem[]): GroupedRailTool[] {
  const grouped: GroupedRailTool[] = [];
  for (const item of tools) {
    const prev = grouped[grouped.length - 1];
    if (
      prev &&
      prev.tool === item.tool &&
      prev.label === item.label &&
      !PREVIEWABLE_TOOLS.has(prev.event.toolName) &&
      !PREVIEWABLE_TOOLS.has(item.event.toolName)
    ) {
      prev.count += 1;
      if (item.status === "run") prev.status = "run";
      else if (item.status === "err" && prev.status !== "run") prev.status = "err";
      prev.showProgress = prev.showProgress && item.showProgress;
      continue;
    }
    grouped.push({ ...item, count: 1 });
  }
  return grouped;
}

export function mapPersistedTool(event: ChatToolEvent, index: number): RailToolItem {
  return {
    key: event.toolCallId || `${event.toolName}-${index}`,
    tool: getToolInstrumentLabel(event.toolName, event.input, event.result),
    label: getToolDisplayLabel(event.toolName, event.input, event.result),
    status: "ok",
    showProgress: toolShowsProgressBar(event.toolName),
    event,
  };
}

export function mapStreamingTool(event: StreamingToolEvent): RailToolItem {
  return {
    key: event.toolCallId,
    tool: getToolInstrumentLabel(event.toolName, event.input, event.result),
    label: getToolDisplayLabel(event.toolName, event.input, event.result),
    status: event.status === "running" ? "run" : "ok",
    showProgress: toolShowsProgressBar(event.toolName),
    event,
  };
}
